import { PageHeader } from '../../components/ui/PageHeader.tsx';
import { useEffect, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import { api, post, patch } from '../../lib/api.ts';
import { KIND_LABEL, SOURCE_LABEL, STATUS_LABEL, type ApprovalTypeLite, type Prepared, type StepDef } from '../../lib/approvals.ts';
import { useClientGrid } from '../../lib/grid.ts';
import { ErrorText } from '../../components/AuthShell.tsx';
import { DataTable, type Column } from '../../components/DataTable.tsx';
import { LineEditor } from './LineEditor.tsx';
import { buttonClass } from '../../components/ui/Button.tsx';
import { FormSection, FormField, ftInput } from '../../components/ui/FormTable.tsx';

const btn = buttonClass('primary');
const btnGhost = buttonClass('secondary');
type PreviewStep = Prepared['preview'][number];
const PREVIEW_COLS: Column<PreviewStep>[] = [
  { key: 'orderNo', label: '#', width: '50px', filter: 'none' },
  { key: 'name', label: '단계', width: '140px', filter: 'none' },
  { key: 'kind', label: '유형', width: '80px', filter: 'none', render: (p) => KIND_LABEL[p.kind] },
  { key: 'assignee', label: '결재자', filter: 'none', filterValue: (p) => p.assignee?.name ?? '', render: (p) => <>{p.assignee?.name ?? '—'}{p.delegatedFrom && ` (${p.delegatedFrom.name} 대결)`}{SOURCE_LABEL[p.source] && p.source !== 'user' && ` · ${SOURCE_LABEL[p.source]}`}</> },
  { key: 'initialStatus', label: '결과', width: '160px', filter: 'none', render: (p) => <>{STATUS_LABEL[p.initialStatus] ?? p.initialStatus}{p.note && ` · ${p.note}`}</> },
];

/** 기안: 유형 선택 → 결재선 미리보기·조정 → 제목·내용 → 임시저장 / 제출 */
export function ApprovalNewPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const types = useQuery({ queryKey: ['approval-types'], queryFn: () => api<ApprovalTypeLite[]>('/approvals/types') });
  const [typeCode, setTypeCode] = useState(sp.get('type') ?? '');
  const targetId = sp.get('targetId') ?? undefined;
  const [prep, setPrep] = useState<Prepared | null>(null);
  const [steps, setSteps] = useState<StepDef[]>([]);
  const [title, setTitle] = useState(''); const [body, setBody] = useState('');
  const [error, setError] = useState<unknown>(null); const [busy, setBusy] = useState(false);
  const previewGrid = useClientGrid(prep?.preview, PREVIEW_COLS, { pageSize: 0 });

  useEffect(() => {
    if (!typeCode) { setPrep(null); return; }
    setError(null);
    api<Prepared>(`/approvals/prepare/${typeCode}${targetId ? `?targetId=${targetId}` : ''}`).then((p) => { setPrep(p); setSteps(p.steps); }).catch((e) => { setPrep(null); setError(e); });
  }, [typeCode, targetId]);

  async function save(submit: boolean) {
    setError(null); setBusy(true);
    try {
      const req = await post<{ id: string }>('/approvals', { typeCode, title, body, targetId, steps });
      if (submit) await post(`/approvals/${req.id}/submit`);
      nav(`/approvals/${req.id}`, { replace: true });
    } catch (e) { setError(e); } finally { setBusy(false); }
  }
  const policy = prep?.type.initiatorEditPolicy ?? 'fixed';

  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); save(true); }} className="">
      <PageHeader title="기안" />
      <FormSection title="기본정보" cols={1}>
      <FormField label="결재 유형" required hint={prep ? `${prep.type.description} · 기안자 조정: ${policy === 'fixed' ? '고정' : policy === 'append_only' ? '단계 추가만' : '자유'}` : undefined}>
        <select className={ftInput + ' ft-w-full'} value={typeCode} onChange={(e) => setTypeCode(e.target.value)} required>
          <option value="">— 선택 —</option>
          {types.data?.map((t) => <option key={t.code} value={t.code} disabled={!t.defaultLineComplete}>{t.name}{!t.defaultLineComplete ? ' (결재선 미완성 — 관리자 설정 필요)' : ''}{t.targetType ? ' · 대상 필요' : ''}</option>)}
        </select>
      </FormField>
      {prep && <>
        <FormField label="제목" required><input className={ftInput + ' ft-w-full'} value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} /></FormField>
        <FormField label="내용"><textarea className={ftInput + ' ft-w-full h-40'} value={body} onChange={(e) => setBody(e.target.value)} /></FormField>
      </>}
      </FormSection>
      {prep && (
        <>
          <div className="mb-2 font-semibold text-sm">결재선</div>
          <LineEditor steps={steps} onChange={setSteps} policy={policy} />
          <details className="mt-3 text-sm"><summary className="cursor-pointer text-slate-600">제출 시 해석 결과 미리보기 (기본선 기준)</summary>
            <div className="mt-2"><DataTable columns={PREVIEW_COLS} data={previewGrid.data} state={previewGrid.state} onChange={previewGrid.set} size="sm" compact pagination="none" rowKey={(_p, i) => String(i)} emptyText="단계 없음" /></div>
          </details>
          <div className="mt-4 flex gap-2">
            <button className={btn} disabled={busy}>제출</button>
            <button type="button" className={btnGhost} disabled={busy} onClick={() => save(false)}>임시저장</button>
          </div>
        </>
      )}
      <ErrorText error={error} />
    </form>
  );
}

export { patch as _patch };
